import type { FC } from 'react';

import { useDeviceType, useUserAgentDevice } from '@packages/shared';

import { useSorting } from '../../hooks/useSorting';
import { useFilters } from '../../hooks/useFilters';
import type { SortingOrder, Filter, SelectedFilter } from '../../api/rest/types';
import { ErasedFiltersNotification } from '../ErasedFiltersNotification';
import type { FilterSettingsMobileProps } from '../FilterSettingsMobile';
import { FilterSettingsMobile } from '../FilterSettingsMobile';
import { FilterSettingsDesktop } from '../FilterSettingsDesktop';
import { SelectedFilterValueList } from '../SelectedFilterValueList';

export type FilterSettingsProps = {
  availableFilters: Filter[];
  selectedFilters: SelectedFilter[];
  availableSortingOrders: SortingOrder[];
  showRemovedFiltersNotification?: boolean;
  renderCategoryDialogContent: FilterSettingsMobileProps['renderCategoryDialogContent'];
};

/**
 * Responsive component to handle all filter interactions
 * */
export const FilterSettings: FC<FilterSettingsProps> = ({
  availableFilters,
  selectedFilters,
  availableSortingOrders,
  showRemovedFiltersNotification,
  renderCategoryDialogContent,
}) => {
  const userAgentDevice = useUserAgentDevice();
  const { isMobile } = useDeviceType();

  const { selectedFilterValues, pushFilterChange } = useFilters(selectedFilters);
  const { sortingKey, pushSortingChange } = useSorting(availableSortingOrders);

  return (
    <>
      <ErasedFiltersNotification
        show={showRemovedFiltersNotification}
        onShow={() => pushFilterChange([])}
      />
      {isMobile || userAgentDevice === 'mobile' ? (
        <FilterSettingsMobile
          filters={availableFilters}
          selectedFilterValues={selectedFilterValues}
          sortingOptions={availableSortingOrders}
          selectedSortingKey={sortingKey}
          applyCloseButtonPaddingWorkaround
          onSubmit={pushFilterChange}
          onChangeSortingKey={pushSortingChange}
          renderCategoryDialogContent={renderCategoryDialogContent}
        />
      ) : (
        <FilterSettingsDesktop
          selectedFilterValues={selectedFilterValues}
          filters={availableFilters}
          onSubmit={pushFilterChange}
        />
      )}
      <SelectedFilterValueList selectedFilters={selectedFilters} showColors />
    </>
  );
};
